import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { BoardSearchSimplePickerPanel } from '../../board/board-search-filters/BoardSearchSimplePickerPanel';
import { EcClickUpFilterChip } from '../../experiencia-cliente/components/EcClickUpFilterChip';
import {
  buildEcFilterPopoverStyle,
  EC_FILTER_POPOVER_CLASS,
} from '../../experiencia-cliente/components/experienciaClienteFilterPopoverUtils';
import { computeToolbarFilterPopoverLayout } from '../../board/board-search-filters/toolbarFilterPopoverLayout';
import '../../board/toolbar/BoardClickUpFilterChip.css';
import '../../board/board-search-filters/BoardSearchFilters.css';

export function VchSinglePickerChip({
  chipLabel,
  icon,
  items = [],
  getKey,
  getLabel,
  getSuffix,
  selectedKey,
  onSelect,
  disabled = false,
  showSearch = true,
  searchPlaceholder = 'Buscar',
  popoverId,
  listAriaLabel,
  emptyMessage = 'Nenhum resultado',
  width = 280,
  maxHeight = 320,
  placeholderText = '',
  className = '',
  matchAnchorWidth = false,
  PanelComponent = null,
  filterItem = null,
}) {
  const btnRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [layout, setLayout] = useState(null);

  const updateLayout = useCallback(() => {
    const el = btnRef.current;
    if (!el || !open) {
      setLayout(null);
      return;
    }
    const rect = el.getBoundingClientRect();
    const popWidth = matchAnchorWidth ? Math.round(rect.width) : width;
    setLayout(computeToolbarFilterPopoverLayout(rect, popWidth, maxHeight));
  }, [open, width, maxHeight, matchAnchorWidth]);

  useLayoutEffect(() => {
    updateLayout();
  }, [updateLayout, open]);

  useEffect(() => {
    if (!open) return undefined;
    const onResize = () => updateLayout();
    window.addEventListener('resize', onResize);
    window.addEventListener('scroll', onResize, true);
    return () => {
      window.removeEventListener('resize', onResize);
      window.removeEventListener('scroll', onResize, true);
    };
  }, [open, updateLayout]);

  useEffect(() => {
    if (!open) setSearchTerm('');
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => {
      const pop = document.querySelector(`[data-vch-picker="${popoverId}"]`);
      if (pop?.contains(e.target) || btnRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', onDoc, true);
    return () => document.removeEventListener('mousedown', onDoc, true);
  }, [open, popoverId]);

  const filteredItems = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    if (!showSearch || !q) return items;
    if (filterItem) return items.filter((item) => filterItem(item, q));
    return items.filter((item) => {
      const label = String(getLabel(item) || '').toLowerCase();
      const suffix = getSuffix ? String(getSuffix(item) || '').toLowerCase() : '';
      return label.includes(q) || suffix.includes(q);
    });
  }, [items, searchTerm, showSearch, getLabel, getSuffix, filterItem]);

  const selectedItem = useMemo(
    () => items.find((i) => String(getKey(i)) === String(selectedKey)),
    [items, selectedKey, getKey]
  );
  const triggerValue = selectedItem ? getLabel(selectedItem) : '';
  const hasSelection = Boolean(selectedKey && selectedItem);
  const displayLabel = hasSelection ? chipLabel : (placeholderText || chipLabel);

  const handleToggle = useCallback(
    (item) => {
      onSelect(getKey(item));
      setOpen(false);
    },
    [onSelect, getKey]
  );

  const popoverStyle = buildEcFilterPopoverStyle(layout);
  const Panel = PanelComponent || BoardSearchSimplePickerPanel;

  const chipNode = (
    <>
      <EcClickUpFilterChip
        anchorRef={btnRef}
        label={displayLabel}
        value={triggerValue}
        icon={icon}
        hasSelection={hasSelection}
        isOpen={open}
        disabled={disabled}
        ariaLabel={`Selecionar ${chipLabel}`}
        onClick={() => !disabled && setOpen((o) => !o)}
        showValue={hasSelection}
      />
      {open && popoverStyle
        ? ReactDOM.createPortal(
            <div
              data-vch-picker={popoverId}
              className={EC_FILTER_POPOVER_CLASS}
              style={popoverStyle}
              role="dialog"
              onClick={(e) => e.stopPropagation()}
              onMouseDown={(e) => e.stopPropagation()}
            >
              <Panel
                showSearch={showSearch}
                searchPlaceholder={searchPlaceholder}
                searchTerm={searchTerm}
                onSearchChange={setSearchTerm}
                items={filteredItems}
                getKey={getKey}
                getLabel={getLabel}
                getSuffix={getSuffix}
                selectedKeys={selectedKey ? [String(selectedKey)] : []}
                onToggle={handleToggle}
                emptyMessage={emptyMessage}
                listAriaLabel={listAriaLabel || chipLabel}
              />
            </div>,
            document.body
          )
        : null}
    </>
  );

  if (className) {
    return <div className={className}>{chipNode}</div>;
  }

  return chipNode;
}
